import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import Layout from '../components/layout/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Separator } from '../components/ui/separator';
import { useCart } from '../context/CartContext';
import { createOrder } from '../services/orders';
import { formatCurrency } from '../lib/currency';
import { useToast } from '../hooks/use-toast';
import PaymentLogos from '../components/payment/PaymentLogos';
import MobileStickyBar from '../components/layout/MobileStickyBar';
import { getPageBySlug } from '../services/pages';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const CheckoutPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { cartItems, cartTotal, clearCart } = useCart();

  // Prices are stored in TRY
  const TRY_RATE = 1;
  const cartTotalTRY = cartTotal * TRY_RATE;
  const shipping = cartTotalTRY >= 500 ? 0 : 239.7; // ~8 USD in TRY
  const tax = cartTotalTRY * 0.18; // Turkish VAT is 18%
  const orderTotal = cartTotalTRY + shipping + tax;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [consents, setConsents] = useState({
    privacy: false,
    distanceSales: false,
  });
  const [legalModal, setLegalModal] = useState({
    open: false,
    title: '',
    content: '',
    loading: false,
  });

  const openLegalModal = async (slug, fallbackTitle) => {
    setLegalModal({
      open: true,
      title: fallbackTitle,
      content: '',
      loading: true,
    });
    try {
      const page = await getPageBySlug(slug);
      setLegalModal((prev) => ({
        ...prev,
        title: page?.title || fallbackTitle,
        content: page?.content || '<p>İçerik bulunamadı.</p>',
        loading: false,
      }));
    } catch (error) {
      console.error('Error fetching legal page:', error);
      setLegalModal((prev) => ({
        ...prev,
        content: '<p>Sözleşme içeriği yüklenemedi. Lütfen tekrar deneyin.</p>',
        loading: false,
      }));
    }
  };

  // Validation schema
  const validationSchema = Yup.object({
    fullName: Yup.string()
      .required(t('validation.required')),
    email: Yup.string()
      .email(t('validation.email'))
      .required(t('validation.required')),
    phone: Yup.string()
      .matches(/^[0-9]{10,11}$/, t('validation.phone'))
      .required(t('validation.required')),
    address: Yup.string()
      .required(t('validation.required')),
    city: Yup.string()
      .required(t('validation.required')),
    district: Yup.string()
      .required(t('validation.required')),
  });

  const formik = useFormik({
    initialValues: {
      fullName: '',
      email: '',
      phone: '',
      address: '',
      city: '',
      district: '',
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        // Validate cart is not empty
        if (!cartItems || cartItems.length === 0) {
          toast({
            title: 'Hata',
            description: 'Sepetiniz boş. Lütfen ürün ekleyin.',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }

        // Validate customer info fields
        const customerInfo = {
          fullName: values.fullName?.trim(),
          email: values.email?.trim(),
          phone: values.phone?.trim(),
          address: values.address?.trim(),
          city: values.city?.trim(),
          district: values.district?.trim(),
        };

        // Check for undefined or empty required fields
        const missingFields = [];
        if (!customerInfo.fullName) missingFields.push('Ad Soyad');
        if (!customerInfo.email) missingFields.push('E-posta');
        if (!customerInfo.phone) missingFields.push('Telefon');
        if (!customerInfo.address) missingFields.push('Adres');
        if (!customerInfo.city) missingFields.push('Şehir');
        if (!customerInfo.district) missingFields.push('İlçe');

        if (missingFields.length > 0) {
          toast({
            title: 'Hata',
            description: `Lütfen şu alanları doldurun: ${missingFields.join(', ')}`,
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }

        if (!consents.privacy || !consents.distanceSales) {
          toast({
            title: 'Sözleşme Onayı Gerekli',
            description: 'Siparişe devam etmek için Gizlilik ve Mesafeli Satış sözleşmelerini onaylayın.',
            variant: 'destructive',
          });
          setIsSubmitting(false);
          return;
        }

        // Prepare order items with validation
        const orderItems = cartItems.map((item) => {
          if (!item.id || !item.name || !item.price || !item.quantity) {
            throw new Error('Geçersiz ürün bilgisi');
          }
          return {
            productId: item.id,
            name: item.name,
            price: item.price * TRY_RATE,
            quantity: item.quantity,
            size: item.size || null,
            color: item.color || null,
            image: item.images?.[0] || '',
          };
        });

        // Prepare order data
        const orderData = {
          customerInfo,
          items: orderItems,
          total: orderTotal,
          subtotal: cartTotalTRY,
          shipping,
          tax,
          discount: 0,
        };

        // Log order data for debugging (remove in production)
        console.log('Creating order with data:', orderData);

        // Create order
        const order = await createOrder(orderData);

        // Clear cart
        clearCart();

        // Redirect to confirmation page
        navigate(`/order-confirmation/${order.orderNumber}`);
      } catch (error) {
        console.error('Error creating order:', error);
        toast({
          title: 'Hata',
          description: error.message || 'Sipariş oluşturulamadı. Lütfen bilgileri kontrol edin ve tekrar deneyin.',
          variant: 'destructive',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  if (cartItems.length === 0) {
    return (
      <Layout>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <h1 className="text-2xl font-semibold mb-4">{t('cart.empty')}</h1>
          <Button onClick={() => navigate('/')}>{t('common.shopNow')}</Button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 lg:pb-8">
        <h1 className="text-3xl font-semibold mb-8">{t('checkout.title')}</h1>

        <form onSubmit={formik.handleSubmit}>
          <div className="lg:grid lg:grid-cols-3 lg:gap-8">
            {/* Shipping Information */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg p-6 border mb-6">
                <h2 className="text-xl font-semibold mb-6">{t('checkout.shippingInfo')}</h2>

                <div className="space-y-4">
                  {/* Full Name */}
                  <div>
                    <Label htmlFor="fullName">{t('checkout.fullName')} *</Label>
                    <Input
                      id="fullName"
                      name="fullName"
                      value={formik.values.fullName}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.fullName && formik.touched.fullName ? 'border-red-500' : ''}
                    />
                    {formik.errors.fullName && formik.touched.fullName && (
                      <p className="text-red-500 text-sm mt-1">{formik.errors.fullName}</p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <Label htmlFor="email">{t('checkout.email')} *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formik.values.email}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.email && formik.touched.email ? 'border-red-500' : ''}
                    />
                    {formik.errors.email && formik.touched.email && (
                      <p className="text-red-500 text-sm mt-1">{formik.errors.email}</p>
                    )}
                  </div>

                  {/* Phone */}
                  <div>
                    <Label htmlFor="phone">{t('checkout.phone')} *</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      placeholder="5XX XXX XX XX"
                      value={formik.values.phone}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={formik.errors.phone && formik.touched.phone ? 'border-red-500' : ''}
                    />
                    {formik.errors.phone && formik.touched.phone && (
                      <p className="text-red-500 text-sm mt-1">{formik.errors.phone}</p>
                    )}
                  </div>

                  {/* Address */}
                  <div>
                    <Label htmlFor="address">{t('checkout.address')} *</Label>
                    <textarea
                      id="address"
                      name="address"
                      rows={3}
                      value={formik.values.address}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      className={`w-full px-3 py-2 border rounded-md ${
                        formik.errors.address && formik.touched.address ? 'border-red-500' : ''
                      }`}
                    />
                    {formik.errors.address && formik.touched.address && (
                      <p className="text-red-500 text-sm mt-1">{formik.errors.address}</p>
                    )}
                  </div>

                  {/* City and District */}
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="city">{t('checkout.city')} *</Label>
                      <Input
                        id="city"
                        name="city"
                        value={formik.values.city}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={formik.errors.city && formik.touched.city ? 'border-red-500' : ''}
                      />
                      {formik.errors.city && formik.touched.city && (
                        <p className="text-red-500 text-sm mt-1">{formik.errors.city}</p>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="district">{t('checkout.district')} *</Label>
                      <Input
                        id="district"
                        name="district"
                        value={formik.values.district}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        className={formik.errors.district && formik.touched.district ? 'border-red-500' : ''}
                      />
                      {formik.errors.district && formik.touched.district && (
                        <p className="text-red-500 text-sm mt-1">{formik.errors.district}</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Order Summary */}
            <div className="lg:mt-0 mt-8">
              <div className="bg-gray-50 rounded-lg p-6 sticky top-28">
                <h2 className="text-lg font-semibold mb-4">{t('checkout.orderSummary')}</h2>

                {/* Order Items */}
                <div className="space-y-3 mb-4">
                  {cartItems.map((item) => (
                    <div key={item.cartId} className="flex gap-3">
                      <img
                        src={item.images[0]}
                        alt={item.name}
                        className="w-16 h-20 object-cover rounded"
                      />
                      <div className="flex-1">
                        <p className="text-sm font-medium">{item.name}</p>
                        <p className="text-xs text-gray-500">
                          {t('product.size')}: {item.size} | {t('product.color')}: {item.color}
                        </p>
                        <p className="text-sm">
                          {item.quantity} x {formatCurrency(item.price * TRY_RATE)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>

                <Separator className="my-4" />

                {/* Totals */}
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('cart.subtotal')}</span>
                    <span>{formatCurrency(cartTotalTRY)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('cart.shipping')}</span>
                    <span>
                      {shipping === 0 ? (
                        <span className="text-green-600">{t('cart.freeShipping')}</span>
                      ) : (
                        formatCurrency(shipping)
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">{t('cart.tax')}</span>
                    <span>{formatCurrency(tax)}</span>
                  </div>
                </div>

                <Separator className="my-4" />

                <div className="flex justify-between font-semibold text-lg">
                  <span>{t('cart.total')}</span>
                  <span>{formatCurrency(orderTotal)}</span>
                </div>

                <Button
                  type="submit"
                  className="w-full mt-6 bg-black hover:bg-gray-800"
                  size="lg"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? t('common.loading') : t('checkout.placeOrder')}
                </Button>
                <div className="mt-4 space-y-3 text-sm">
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={consents.distanceSales}
                      onChange={(e) =>
                        setConsents((prev) => ({ ...prev, distanceSales: e.target.checked }))
                      }
                      className="mt-1"
                    />
                    <span>
                      <button
                        type="button"
                        className="underline"
                        onClick={() =>
                          openLegalModal(
                            'mesafeli-satis-sozlesmesi',
                            'Mesafeli Satış Sözleşmesi'
                          )
                        }
                      >
                        Mesafeli Satış Sözleşmesi
                      </button>{' '}
                      metnini okudum ve kabul ediyorum.
                    </span>
                  </label>
                  <label className="flex items-start gap-2">
                    <input
                      type="checkbox"
                      checked={consents.privacy}
                      onChange={(e) =>
                        setConsents((prev) => ({ ...prev, privacy: e.target.checked }))
                      }
                      className="mt-1"
                    />
                    <span>
                      <button
                        type="button"
                        className="underline"
                        onClick={() => openLegalModal('gizlilik-sozlesmesi', 'Gizlilik Sözleşmesi')}
                      >
                        Gizlilik Sözleşmesi
                      </button>{' '}
                      metnini okudum ve kabul ediyorum.
                    </span>
                  </label>
                </div>
                <p className="mt-4 text-xs text-gray-600 border rounded-md p-3 bg-gray-50">
                  Sipariş ve ödeme bilgileriniz 256-bit SSL ile şifrelenerek iletilir.
                </p>
                <div className="mt-3 rounded-md border bg-white p-3">
                  <p className="text-xs font-medium text-gray-700">
                    Visa, MasterCard ve iyzico ile güvenli ödeme
                  </p>
                  <PaymentLogos variant="checkout" showIyzicoLabel />
                </div>
              </div>
            </div>
          </div>
        </form>
      </div>
      <MobileStickyBar
        price={formatCurrency(orderTotal)}
        priceLabel={t('cart.total')}
        ctaLabel={isSubmitting ? t('common.loading') : t('checkout.placeOrder')}
        onCtaClick={() => formik.handleSubmit()}
        disabled={isSubmitting}
        loading={isSubmitting}
      />
      <Dialog
        open={legalModal.open}
        onOpenChange={(open) => setLegalModal((prev) => ({ ...prev, open }))}
      >
        <DialogContent className="max-w-2xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle>{legalModal.title || 'Sözleşme'}</DialogTitle>
          </DialogHeader>
          <div className="overflow-y-auto pr-1 max-h-[65vh]">
            {legalModal.loading ? (
              <p className="text-sm text-gray-600">Yükleniyor...</p>
            ) : (
              <div
                className="page-content prose prose-sm max-w-none"
                dangerouslySetInnerHTML={{ __html: legalModal.content }}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CheckoutPage;
